// ThreatModelFindingsPanel — Phase 4 G3.7 UI (2026-05-29).
//
// Surfaces PEG-THREAT-MODEL-COMPLETENESS-001 resolver findings on
// the G3.7 review surface. The threat_model_author agent's output
// is read-only here — the architect fixes findings either by
// editing phase3/threat-model.md directly OR by adding a waiver to
// phase4/audit-diff.waivers.json. A future sprint may add a per-
// finding override modal here, parallel to the Deployment Decisions
// panel.
//
// Layout (mirrors DataDictionaryFindingsPanel):
//
//   ┌──────────────────────────────────────────────────────────────┐
//   │ Header: regulatory · shape · N findings across M assets      │
//   ├──────────────────────────────────────────────────────────────┤
//   │ Missing-assets group (red border, expanded by default) when  │
//   │ any mandatory asset is absent.                               │
//   │   ⌄ (missing required assets) · 2 findings                   │
//   │     • [high] missing_mandatory_asset · CDE                   │
//   │     • [high] missing_mandatory_asset · SAD                   │
//   ├──────────────────────────────────────────────────────────────┤
//   │ Per-asset accordion. Assets with findings expanded by        │
//   │ default; clean assets collapsed.                             │
//   │   ⌄ Identity · 5 findings (STRIDE gaps)                      │
//   │     • [high] missing_stride_category · Tampering             │
//   │     ...                                                      │
//   │   ⌃ ✓ Cardholder Data Environment (CDE)                      │
//   ├──────────────────────────────────────────────────────────────┤
//   │ Help footer: how to remediate / waive                        │
//   └──────────────────────────────────────────────────────────────┘

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Info,
  Loader2,
  RefreshCw,
  ShieldAlert,
  ShieldOff,
} from 'lucide-react'

import { FINDING_PRESENTATION } from '@/lib/findingPresentation'
import {
  getThreatModelFindings,
  regenerateThreatModel,
} from '@/lib/threatModelFindingsApi'
import {
  TM_INVARIANT_ID,
  TM_KIND_LABEL,
  TM_SEVERITY_PALETTE,
  type ThreatModelFindingRow,
  type ThreatModelFindingsResponse,
} from '@/types/threatModelFindings'
import { WaivedFindingsDisclosure } from './WaivedFindingsDisclosure'
import { WaiveFindingModal } from './WaiveFindingModal'

export interface ThreatModelFindingsPanelProps {
  projectId: string
  runId: number
}

interface GroupedFindings {
  /** Asset section name. ``null`` is the synthetic group that
   *  collects ``missing_mandatory_asset`` + ``no_asset_sections``
   *  findings — those don't belong to any present section. */
  assetName: string | null
  findings: ThreatModelFindingRow[]
}

function groupByAsset(
  rows: ThreatModelFindingRow[],
  inventory: string[],
): GroupedFindings[] {
  const byAsset = new Map<string | null, ThreatModelFindingRow[]>()
  // Pre-seed inventory so clean assets still appear.
  for (const a of inventory) byAsset.set(a, [])
  // Bucket findings: ``missing_mandatory_asset`` /
  // ``no_asset_sections`` go to the null group; STRIDE findings
  // attach to their asset_name (which is always present in the
  // inventory because the resolver only fires STRIDE checks on
  // sections it parsed).
  for (const f of rows) {
    let key: string | null = f.asset_name
    if (
      f.finding_kind === 'missing_mandatory_asset' ||
      f.finding_kind === 'no_asset_sections'
    ) {
      key = null
    }
    if (!byAsset.has(key)) byAsset.set(key, [])
    byAsset.get(key)!.push(f)
  }
  // Order: null group (missing-assets / no-sections) first;
  // then assets with findings (count desc); then clean assets in
  // inventory order (preserved by Map iteration when count ties
  // resolve to alphabetical).
  const groups: GroupedFindings[] = []
  if (byAsset.has(null)) {
    groups.push({ assetName: null, findings: byAsset.get(null)! })
    byAsset.delete(null)
  }
  const entries = Array.from(byAsset.entries())
  entries.sort((a, b) => {
    const ca = a[1].length
    const cb = b[1].length
    if (ca !== cb) return cb - ca
    return (a[0] || '').localeCompare(b[0] || '')
  })
  for (const [name, findings] of entries) {
    groups.push({ assetName: name, findings })
  }
  return groups
}

function FindingCard({
  row,
  onWaive,
}: {
  row: ThreatModelFindingRow
  onWaive: () => void
}) {
  const sev = TM_SEVERITY_PALETTE[row.severity]
  return (
    <div className="rounded border border-slate-700/50 bg-slate-950/40 p-2.5 text-sm">
      <div className="mb-1 flex flex-wrap items-center gap-1.5">
        <span
          className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10.5px] font-medium ${sev.chipClass}`}
        >
          {sev.label}
        </span>
        <span className="text-[10.5px] uppercase tracking-wide text-slate-500">
          {TM_KIND_LABEL[row.finding_kind]}
        </span>
        {row.stride_category && (
          <span className="rounded border border-indigo-800/40 bg-indigo-950/30 px-1.5 py-0.5 font-mono text-[10.5px] text-indigo-200">
            {row.stride_category}
          </span>
        )}
        {row.asset_name && row.finding_kind === 'missing_mandatory_asset' && (
          <span className="font-mono text-xs text-slate-300">
            {row.asset_name}
          </span>
        )}
      </div>
      <div className="text-slate-200">
        {FINDING_PRESENTATION[row.finding_kind].whyItMatters}
      </div>
      {row.suggested_fix && (
        <div className="mt-1.5 rounded border border-emerald-900/30 bg-emerald-950/15 p-1.5 text-xs text-emerald-200/85">
          <strong className="text-emerald-300">Suggested: </strong>
          {row.suggested_fix}
        </div>
      )}
      {/* Pipeline detail demoted to a collapsible disclosure (legibility
          pass) — the raw resolver message, citations, and rule source. */}
      <details className="mt-1.5 text-[10.5px] text-slate-500">
        <summary className="cursor-pointer select-none text-slate-400">
          Developer detail
        </summary>
        <div className="mt-1 text-slate-400">{row.message}</div>
        {row.cites.length > 0 && (
          <div className="mt-1 text-slate-400">
            cite: {row.cites.join(' · ')}
          </div>
        )}
        <div className="mt-1">
          rule source: <span className="font-mono">{row.rule_source}</span>
        </div>
      </details>
      {/* Resolution action — in-UI waive instead of hand-authoring JSON. */}
      <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-800/50 pt-2">
        <button
          type="button"
          onClick={onWaive}
          className="inline-flex items-center gap-1 rounded border border-amber-700/40 bg-amber-950/20 px-2 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-900/30"
        >
          <ShieldOff className="h-3 w-3" /> Waive
        </button>
      </div>
    </div>
  )
}

function AssetGroup({
  group,
  defaultExpanded,
  onWaive,
}: {
  group: GroupedFindings
  defaultExpanded: boolean
  onWaive: (row: ThreatModelFindingRow) => void
}) {
  const [open, setOpen] = useState(defaultExpanded)
  const findingsCount = group.findings.length
  const isClean = findingsCount === 0
  const isSyntheticGroup = group.assetName === null
  // Pick a label for the synthetic group based on the findings
  // it contains. ``no_asset_sections`` is its own structural
  // defect; otherwise it's a missing-mandatory-asset bucket.
  const hasNoSections = group.findings.some(
    (f) => f.finding_kind === 'no_asset_sections',
  )
  const syntheticLabel = hasNoSections
    ? '(no asset sections found)'
    : '(missing required assets)'
  return (
    <div
      className={`rounded-md border bg-slate-900/40 overflow-hidden ${
        isSyntheticGroup
          ? 'border-red-800/50'
          : isClean
            ? 'border-slate-800/50'
            : 'border-amber-800/40'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-900/60"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <span className="font-mono text-sm font-semibold text-slate-100">
          {group.assetName ?? syntheticLabel}
        </span>
        {isClean ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-300">
            <CheckCircle2 className="h-3 w-3" /> full STRIDE
          </span>
        ) : (
          <span
            className={`inline-flex items-center gap-1 text-xs ${
              isSyntheticGroup ? 'text-red-300' : 'text-amber-300'
            }`}
          >
            <AlertTriangle className="h-3 w-3" />
            {findingsCount} {findingsCount === 1 ? 'finding' : 'findings'}
          </span>
        )}
      </button>
      {open && !isClean && (
        <div className="flex flex-col gap-2 px-3 pb-3 pt-1">
          {group.findings.map((f, idx) => (
            <FindingCard
              key={`${f.finding_kind}-${f.stride_category ?? ''}-${idx}`}
              row={f}
              onWaive={() => onWaive(f)}
            />
          ))}
        </div>
      )}
      {open && isClean && !isSyntheticGroup && (
        // A clean asset has no findings to list; without this, expanding
        // it shows an empty body and the user can't tell "full STRIDE"
        // from "nothing loaded". Confirm the coverage explicitly.
        <div className="px-3 pb-3 pt-1 text-xs text-slate-400">
          All six STRIDE categories — Spoofing, Tampering, Repudiation,
          Information disclosure, Denial of service, Elevation of
          privilege — are covered for this asset. No gaps found.
        </div>
      )}
    </div>
  )
}

export function ThreatModelFindingsPanel({
  projectId,
  runId,
}: ThreatModelFindingsPanelProps) {
  const queryKey = ['threat-model-findings', projectId, runId]
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery<ThreatModelFindingsResponse>({
    queryKey,
    queryFn: () => getThreatModelFindings(projectId, runId),
  })

  const [waiving, setWaiving] = useState<ThreatModelFindingRow | null>(null)

  // G3.7 "Regenerate" — re-run the Security Architect to author every
  // currently-missing mandatory asset, then refresh the panel from the
  // regenerated model. One LLM call server-side (~30–60 s).
  const regenerate = useMutation({
    mutationFn: () => regenerateThreatModel(projectId, runId, []),
    onSuccess: (fresh) => {
      queryClient.setQueryData(queryKey, fresh)
      queryClient.invalidateQueries({ queryKey })
    },
    // The regeneration is a ~1–2 min server-side LLM call. If a proxy /
    // ingress times the request out the work may still have completed,
    // so refetch the findings on error too — the panel then reflects the
    // true state instead of stranding the user on a stale error.
    onError: () => queryClient.invalidateQueries({ queryKey }),
  })

  const activeFindings = useMemo(
    () => (data?.findings ?? []).filter((f) => !f.waived),
    [data],
  )
  const waivedFindings = useMemo(
    () => (data?.findings ?? []).filter((f) => f.waived),
    [data],
  )

  const groups = useMemo(() => {
    if (!data) return [] as GroupedFindings[]
    return groupByAsset(activeFindings, data.asset_inventory)
  }, [data, activeFindings])

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading threat-model findings…
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded border border-red-800 bg-red-950/30 p-3 text-sm text-red-200">
        Failed to load threat-model findings:{' '}
        {String((error as Error).message)}
      </div>
    )
  }
  if (!data) return null

  if (!data.threat_model_present) {
    return (
      <div className="rounded border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-300">
        <div className="mb-1 flex items-center gap-2 text-slate-200">
          <Info className="h-4 w-4 text-sky-400" />
          Phase 3 hasn't shipped threat-model.md yet
        </div>
        <p className="text-xs text-slate-400">
          The threat_model_author agent runs in Phase 3. Once it produces{' '}
          <code className="rounded bg-slate-800 px-1 py-0.5">
            phase3/threat-model.md
          </code>
          , this panel will show any mandate-asset misses or per-asset
          STRIDE coverage gaps the resolver detects.
        </p>
      </div>
    )
  }

  const totalFindings = activeFindings.length
  const assetsWithFindings = groups.filter(
    (g) => g.findings.length > 0 && g.assetName !== null,
  ).length
  const missingAssetCount = activeFindings.filter(
    (f) => f.finding_kind === 'missing_mandatory_asset',
  ).length

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-wrap items-baseline gap-3 text-xs text-slate-400">
        {data.regulatory.length > 0 && (
          <span>regulatory: {data.regulatory.join(', ')}</span>
        )}
        {data.shape && <span>shape: {data.shape}</span>}
        <span>
          {data.asset_inventory.length}{' '}
          {data.asset_inventory.length === 1 ? 'asset' : 'assets'} in threat
          model
        </span>
        {missingAssetCount > 0 && (
          <span className="text-red-300">
            <ShieldAlert className="mr-1 inline-block h-3 w-3" />
            {missingAssetCount} required asset
            {missingAssetCount === 1 ? '' : 's'} missing
          </span>
        )}
        {totalFindings > 0 ? (
          <span className="text-amber-300">
            {totalFindings} {totalFindings === 1 ? 'finding' : 'findings'}
            {assetsWithFindings > 0 && (
              <> across {assetsWithFindings} asset(s)</>
            )}
          </span>
        ) : (
          <span className="text-emerald-300">
            <CheckCircle2 className="mr-1 inline-block h-3 w-3" />
            clean
          </span>
        )}
      </div>

      {/* Regenerate — one-click fix for missing mandatory assets. */}
      {missingAssetCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded border border-sky-900/50 bg-sky-950/20 p-3">
          <button
            type="button"
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending}
            className="inline-flex items-center gap-1.5 rounded bg-sky-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {regenerate.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="h-3.5 w-3.5" />
            )}
            {regenerate.isPending
              ? 'Authoring the missing assets…'
              : `Regenerate ${missingAssetCount} missing asset${
                  missingAssetCount === 1 ? '' : 's'
                }`}
          </button>
          <span className="text-xs text-slate-400">
            {regenerate.isPending
              ? 'Re-running the security assistant — this can take a minute or two.'
              : 'Have the security assistant write a full STRIDE analysis for the missing asset(s), then re-check.'}
          </span>
          {regenerate.isError && (
            <span className="flex items-center gap-1 text-xs text-red-300">
              <AlertTriangle className="h-3 w-3" />
              Couldn't regenerate: {String(
                (regenerate.error as Error).message,
              )}
            </span>
          )}
        </div>
      )}

      {/* Groups */}
      {groups.length === 0 ? (
        <div className="rounded border border-slate-700 bg-slate-900/40 p-3 text-sm text-slate-400">
          No asset sections in the threat model yet.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {groups.map((g) => (
            <AssetGroup
              key={g.assetName ?? '__synthetic__'}
              group={g}
              defaultExpanded={g.findings.length > 0}
              onWaive={setWaiving}
            />
          ))}
        </div>
      )}

      {/* Waived findings — accepted, collapsed out of the way. */}
      <WaivedFindingsDisclosure
        items={waivedFindings.map((f, idx) => ({
          id: `${f.finding_kind}-${f.stride_category ?? ''}-${idx}`,
          label: `${TM_KIND_LABEL[f.finding_kind]}${
            f.asset_name ? ` · ${f.asset_name}` : ''
          }${f.stride_category ? ` · ${f.stride_category}` : ''}`,
          justification: f.waiver_justification,
        }))}
      />

      {waiving && (
        <WaiveFindingModal
          projectId={projectId}
          runId={runId}
          invariantId={TM_INVARIANT_ID}
          findingKey={waiving.finding_key}
          findingLabel={`${TM_KIND_LABEL[waiving.finding_kind]}${
            waiving.asset_name ? ` · ${waiving.asset_name}` : ''
          }${waiving.stride_category ? ` · ${waiving.stride_category}` : ''}`}
          refetchQueryKey={queryKey}
          onClose={() => setWaiving(null)}
        />
      )}

      {/* Help footer */}
      <div className="rounded border border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-400">
        <div className="mb-1 flex items-center gap-2 text-slate-300">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          How to clear these findings
        </div>
        <p>
          <strong className="text-sky-200">Missing required asset:</strong>{' '}
          click <strong>Regenerate</strong> to have the security assistant
          write a full STRIDE analysis for the missing asset(s) into the
          threat model, then re-check automatically.{' '}
          <strong className="text-amber-200">
            Out of scope or a false positive:
          </strong>{' '}
          click <strong>Waive</strong> on the finding and record why — the
          reason is saved to the audit trail and the pre-export gate stops
          blocking on it. Either way, you don't edit any files by hand.
        </p>
      </div>
    </div>
  )
}
